import React, { useState } from 'react';
import { DownloadSimple, Trash, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { accountAPI } from '../api/api';
import { toast } from '../hooks/use-toast';

export const AccountPrivacyCard = () => {
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await accountAPI.exportData();
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-constructhelp-data.json';
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Export ready', description: 'Your data has been downloaded as JSON.' });
    } catch {
      toast({ title: 'Export failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await accountAPI.deleteAccount(password);
      localStorage.clear();
      window.location.href = '/';
    } catch (e) {
      toast({
        title: 'Deletion failed',
        description: e.response?.data?.detail || 'Please check your password.',
        variant: 'destructive',
      });
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-600 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A]" data-testid="account-privacy-card">
      <h3 className="font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-1">Privacy & Data</h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">Download everything we store about you, or permanently delete your account.</p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] font-bold text-sm rounded-lg border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50"
          data-testid="export-data-btn"
        >
          <DownloadSimple size={18} weight="bold" />
          {exporting ? 'Preparing...' : 'Export My Data'}
        </button>
        <button
          onClick={() => setDeleteOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-neutral-700 hover:bg-red-50 text-red-600 font-bold text-sm rounded-lg border-2 border-red-500 shadow-[2px_2px_0px_#DC2626] active:translate-y-0.5 active:shadow-none transition-all"
          data-testid="delete-account-btn"
        >
          <Trash size={18} weight="bold" /> Delete Account
        </button>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="border-4 border-red-600" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 font-black uppercase">
              <Warning size={22} weight="fill" /> Delete your account?
            </DialogTitle>
            <DialogDescription>
              This is permanent. Your profile will be erased, personal data scrubbed, and you'll be logged out everywhere. Export your data first if you want a copy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-bold text-neutral-700 dark:text-neutral-300">Your password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full h-11 px-3 border-2 border-neutral-300 rounded-lg focus:outline-none focus:border-red-500"
                data-testid="delete-password-input"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-neutral-700 dark:text-neutral-300">Type <span className="font-mono bg-neutral-100 dark:bg-neutral-700 px-1">DELETE</span> to confirm</label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1 w-full h-11 px-3 border-2 border-neutral-300 rounded-lg focus:outline-none focus:border-red-500"
                data-testid="delete-confirm-input"
              />
            </div>
            <button
              onClick={handleDelete}
              disabled={deleting || confirmText !== 'DELETE' || !password}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wide rounded-lg border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-account-confirm-btn"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete Account'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountPrivacyCard;
